import React, { useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useDgptAlertsFlag } from '../../utils/featureFlags';
import { useAors, useDeleteAor } from 'hooks/useAors';
import Alert from 'react-bootstrap/Alert';
import { AddUpdateAor } from './AddUpdateAor';
import { Card, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { AreaOfResponsibility } from '../../types/aor';

const ManageLocation: React.FC = () => {
  const { data: allAor = [] } = useAors();
  const dgptAlertsEnabled = useDgptAlertsFlag();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [aorToDelete, setAorToDelete] = useState<string | null>(null);
  const [editAorData, setEditAorData] = useState<AreaOfResponsibility | null>(
    null
  );
  const deleteAor = useDeleteAor();

  const handleEdit = (aor: AreaOfResponsibility) => {
    setEditAorData(aor);
    setShowAddDialog(true);
  };

  const handleDelete = (id: string) => {
    setAorToDelete(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (aorToDelete) {
      deleteAor.mutate(aorToDelete);
      setShowDeleteDialog(false);
      setAorToDelete(null);
    }
  };

  return (
    <div className="bg-gray-800 rounded p-4">
      {allAor.length === 0 && dgptAlertsEnabled && (
        <Alert variant="warning" className="mb-3">
          <strong>
            At least one Area of Responsibility is required for AI Alerts.
          </strong>
        </Alert>
      )}
      <div className="d-flex align-items-center mb-4">
        <h6 className="text-uppercase m-0">
          Configure Areas of Responsibility
        </h6>
      </div>
      <hr className="dashed my-3" />

      {allAor.length > 0 && (
        <div className="mb-4">
          {allAor.map((aor) => (
            <Card key={aor.id} className="mb-3 bg-gray-700">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{aor.name}</h6>
                    {aor.description && (
                      <p className="mb-0 text-muted">{aor.description}</p>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <StylishNewButton
                      type="button"
                      className="button--secondary button--icon"
                      onClick={() => handleEdit(aor)}
                    >
                      <FaEdit />
                    </StylishNewButton>
                    <StylishNewButton
                      type="button"
                      className="button--secondary button--icon"
                      onClick={() => handleDelete(aor.id)}
                    >
                      <FaTrash />
                    </StylishNewButton>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <div className="button-group justify-content-end">
        <StylishNewButton
          type="button"
          className="button--primary w-100 w-md-auto"
          onClick={() => {
            setEditAorData(null);
            setShowAddDialog(true);
          }}
        >
          {`Add Item`}
        </StylishNewButton>
      </div>

      <AddUpdateAor
        show={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setEditAorData(null);
        }}
        mode={editAorData ? 'Edit' : 'Add'}
        editAorData={editAorData}
      />

      <Modal
        show={showDeleteDialog}
        onHide={() => {
          setShowDeleteDialog(false);
          setAorToDelete(null);
        }}
        centered
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Area of Responsibility?
        </Modal.Body>
        <Modal.Footer>
          <div className="button-group">
            <StylishNewButton
              type="button"
              className="button--secondary button--reverse"
              onClick={() => {
                setShowDeleteDialog(false);
                setAorToDelete(null);
              }}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton
              type="button"
              className="button--primary"
              onClick={confirmDelete}
            >
              Delete
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageLocation;
